import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  message,
  Typography,
  Row,
  Col,
  GetProp,
  UploadProps,
  Alert,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import LogoUpload from "components/logo-upload";
import { useState, useEffect, useMemo, useRef } from "react";
import venueWorkingHours from "../collab/weekHoursDefault.json";
import { PlusCircleOutlined, DeleteFilled } from "@ant-design/icons";
import {
  generateBusinessDescriptionByWebsite,
  obtainBusinessLogoByWebsite,
  uploadLogo,
  removeLogo,
  updateBusinessInfo,
  finishBusinessOnboarding,
} from "services/SauceService";
import ConnectSocial from "components/connect-social";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "components/loader/Loader";
import { getBusinessInfo } from "redux-context/business";
import VenueFormFields from "components/venue-form-fields/VenueFormFields";
import { normalizeWebsiteURL } from "../../helpers/url-helpers";
import { scrollToTop } from "../../helpers/scroll-helpers";
import { isMobile } from "react-device-detect";
import {
  trackWelcomeContinueClicked,
  trackWelcomeLogoUploadClicked,
  trackWelcomePageViewed,
} from "analytics";
import "./OnboardingForm.scss";
import { Venue } from "interfaces/venue";
import "./OnboardingForm.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PaywallSource } from "components/paywall/Paywall";
import { faCrown } from "@fortawesome/free-solid-svg-icons";
import { usePaywall } from "components/paywall/paywallContext";
import { SubscriptionType } from "enums/SubscriptionType";

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const OnboardingForm = () => {
  const [current, setCurrent] = useState(0);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [websiteLoading, setWebsiteLoading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [file, setFile] = useState<any>(undefined);
  const [lastProcessedWebsite, setLastProcessedWebsite] = useState<string>("");
  const [hasBlurredWebsite, setHasBlurredWebsite] = useState(false);
  const { openPaywall } = usePaywall();

  const {
    business,
    loading: businessLoading,
    subscription,
  } = useSelector((state: any) => state.business);
  const { loggedUser } = useSelector((state: any) => state.user);

  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;

  const formContentRef = useRef<HTMLDivElement>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const hasTrackedWelcomePageViewedRef = useRef(false);

  const analyticsContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId: business?._id ?? null,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType: subscription?.planName ?? null,
      currency:
        subscription?.stripeDetails?.nextPaymentDetails?.currency ?? null,
    }),
    [
      business?._id,
      location.pathname,
      location.search,
      loggedUser?.email,
      loggedUser?.id,
      subscription?.planName,
      subscription?.stripeDetails?.nextPaymentDetails?.currency,
    ],
  );

  useEffect(() => {
    if (isMobile) return;

    const container = formContainerRef.current;
    const scrollEl = formContentRef.current;
    if (!container || !scrollEl) return;

    const handleWheel = (e: WheelEvent) => {
      const isOverScrollArea =
        scrollEl === e.target || scrollEl.contains(e.target as Node);
      if (isOverScrollArea) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      const canScrollUp = scrollTop > 0 && e.deltaY < 0;
      const canScrollDown =
        scrollTop < scrollHeight - clientHeight && e.deltaY > 0;
      if (canScrollUp || canScrollDown) {
        e.preventDefault();
        scrollEl.scrollTop += e.deltaY;
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    if (current !== 0) {
      return;
    }

    if (hasTrackedWelcomePageViewedRef.current) {
      return;
    }

    hasTrackedWelcomePageViewedRef.current = true;
    trackWelcomePageViewed(analyticsContext);
  }, [analyticsContext, current]);

  const isValidURL = (rawUrl: string): boolean =>
    normalizeWebsiteURL(rawUrl) !== null;

  const handleWebsiteBlur = async (ev: React.FocusEvent<HTMLInputElement>) => {
    setHasBlurredWebsite(true);
    const websiteInput = ev.target.value.trim();
    if (!websiteInput) return;

    const formValues = form.getFieldsValue(true);

    const normalizedWebsiteURL = normalizeWebsiteURL(websiteInput);

    if (!normalizedWebsiteURL) {
      message.warning(
        "Please enter a valid URL (e.g., https://www.example.com)",
      );
      return;
    }

    const normalizedBusinessWebsite = normalizeWebsiteURL(
      business?.website ?? "",
    );

    if (
      normalizedWebsiteURL === lastProcessedWebsite ||
      normalizedWebsiteURL === normalizedBusinessWebsite
    )
      return;

    setWebsiteLoading(true);
    try {
      const currentDescription = formValues.aboutTheBrand;
      const currentLogoUrl = imageUrl;
      const promises: any[] = [];

      if (!currentDescription) {
        promises.push(
          generateBusinessDescriptionByWebsite({
            websiteURL: normalizedWebsiteURL,
          }).then((response) => ({
            type: "aboutTheBrand",
            data: response,
          })),
        );
      }

      if (!currentLogoUrl) {
        promises.push(
          obtainBusinessLogoByWebsite({
            websiteURL: normalizedWebsiteURL,
          }).then((response) => ({ type: "logo", data: response })),
        );
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);

        let newValues: any = {};
        results.forEach((result) => {
          if (result.type === "aboutTheBrand") {
            newValues.aboutTheBrand = result.data.description;
          } else if (result.type === "logo") {
            newValues.logoUrl = result.data.logoUrl;
            setImageUrl(result.data.logoUrl);
          }
        });

        form.setFieldsValue({
          ...formValues,
          ...newValues,
        });
      }

      setLastProcessedWebsite(normalizedWebsiteURL);
    } catch (error) {
      console.error("Error fetching business data:", error);
    } finally {
      setWebsiteLoading(false);
    }
  };

  const handleLogoChange = (
    selectedFile: any,
    previewUrl: string | undefined,
  ) => {
    if (typeof previewUrl !== "string" && previewUrl !== undefined) {
      console.warn(
        "LogoUpload onChange received invalid previewUrl:",
        previewUrl,
      );
      console.warn("selectedFile:", selectedFile);
      return;
    }
    if (previewUrl && !previewUrl.startsWith("data:image/")) {
      console.warn("Preview URL doesn't look like a base64 image:", previewUrl);
      return;
    }
    setFile(selectedFile);
    setImageUrl(previewUrl || "");
  };

  const handleUpload = async (file: FileType) => {
    const params = { logo: file, businessId: business._id };
    try {
      await uploadLogo(params);
    } catch (error) {
      console.error(error);
      message.error("Failed to upload logo");
    }
  };

  const handleRemoveImage = async () => {
    try {
      await removeLogo({ businessId: business._id });
    } catch (error) {
      console.error(error);
    }
  };

  const handleFinish = async () => {
    setWebsiteLoading(true);
    try {
      const { venues }: { venues: Venue[] } = await form.validateFields();

      const hasVenueClosedEveryDay = venues.some((venue) => {
        return venue.venueWorkingHours?.every((day) => !day.open);
      });

      if (hasVenueClosedEveryDay) {
        message.error(
          "Please set working hours for all venues (no venue can be closed every day)",
        );
        setWebsiteLoading(false);
        return;
      }

      const venuesData = venues.map((x: any) => ({
        ...x,
        placeId: x.googlePlaceId,
        placeName: x.name,
        placeAddress: x.addressDisplay,
        id: x._id,
      }));

      await finishBusinessOnboarding({
        ...business,
        venues: venuesData,
        businessId: business._id,
      });
      await dispatch(getBusinessInfo());
      navigate("/");
    } catch (error) {
      console.error(error);
      message.error("Failed to finish onboarding");
    } finally {
      setWebsiteLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
    setImageUrl(business?.logoUrl);
  }, [business, form]);

  const steps = [
    {
      title: "Business",
      content: (
        <Flex vertical>
          <Flex vertical className="step-section-title-container">
            <p className="step-section-title">Tell us about your business</p>
            <p className="step-section-subtitle">
              We'll use this to match you with creators
            </p>
          </Flex>
          <Form.Item
            label="Business Name"
            rules={[
              {
                required: true,
              },
            ]}
            name="businessName"
          >
            <Input
              placeholder="Enter your business name"
              className="campaign-venues business-name-input"
              autoComplete="off"
              disabled={websiteLoading}
            />
          </Form.Item>
          <Form.Item
            name="website"
            label="Website"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return isValidURL(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Please enter a valid URL!"));
                },
              },
            ]}
          >
            <Input
              placeholder="https://www.yourbusiness.com"
              disabled={websiteLoading}
              onBlur={handleWebsiteBlur}
            />
          </Form.Item>
          <div
            className={`onboarding-description-logo-fields ${hasBlurredWebsite || business?.website ? "visible" : ""}`}
          >
            <div className="onboarding-description-logo-fields-inner">
              <Form.Item name="aboutTheBrand" label="Description">
                <TextArea
                  rows={4}
                  placeholder="Tell us about your business, what you do, and what makes you unique..."
                  disabled={websiteLoading}
                />
              </Form.Item>
              <Form.Item label="Logo">
                <LogoUpload
                  value={imageUrl}
                  onChange={handleLogoChange}
                  onSelectClick={() =>
                    trackWelcomeLogoUploadClicked(analyticsContext)
                  }
                  loading={websiteLoading}
                  maxSizeMB={10}
                />
              </Form.Item>
            </div>
          </div>
        </Flex>
      ),
    },
    {
      title: "Social",
      content: (
        <Flex vertical gap={10}>
          <Flex vertical className="step-section-title-container">
            <p className="step-section-title">Connect Your Social Media</p>
            <p className="step-section-subtitle">
              Linking your social media lets creators tag your brand in posts
              and helps Mustard measure collaboration reach and results.
            </p>
          </Flex>

          <Row gutter={[0, 24]}>
            <Col span={24}>
              <ConnectSocial.Instagram />
            </Col>
            <Col span={24}>
              <ConnectSocial.TikTok />
            </Col>
            <Col span={24}>
              <Alert
                message="Connect all your social media accounts."
                description="When prompted, select every account that will be used for collaborations. If your brand manages separate accounts per location, make sure to include each one."
                type="info"
                showIcon
                className="connect-instagram-alert"
              />
            </Col>
          </Row>
        </Flex>
      ),
    },
    {
      title: "Locations",
      content: (
        <Flex vertical>
          <Flex vertical className="step-section-title-container">
            <p className="step-section-title">Add Your Business Locations</p>
            <p className="step-section-subtitle">
              Add at least one location so creators know where to find you.
            </p>
          </Flex>

          <Form.List name="venues">
            {(fields, { add, remove }) => (
              <div className="venues-list-container">
                {fields.map((field, index) => (
                  <Card
                    key={`venue_${field.name}`}
                    title={
                      <Flex justify="space-between" align="center">
                        <span>{`Location ${index + 1}`}</span>
                        {fields.length > 1 && (
                          <DeleteFilled
                            className="delete-location-icon"
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </Flex>
                    }
                    className="location-card"
                  >
                    <VenueFormFields
                      newSocialMediaFields={true}
                      key={`venue_${field.name}`}
                      namePath={[field.name]}
                      formListNameFullPath={["venues"]}
                      business={business}
                      hiddenCopyWorkingHours
                    />
                  </Card>
                ))}

                {fields.length < subscription?.locationsAllowed && (
                  <span
                    className="add-location-button"
                    onClick={() =>
                      add({
                        pocs: [
                          {
                            name: "",
                            email: "",
                            notifyEmail: false,
                            phone: "",
                          },
                        ],
                        venueWorkingHours,
                      })
                    }
                  >
                    <PlusCircleOutlined />
                    Add another location
                  </span>
                )}
              </div>
            )}
          </Form.List>
        </Flex>
      ),
    },
  ];

  useEffect(() => {
    scrollToTop();
  }, [current]);

  const firstStepHandler = async (values: any) => {
    if (file) await handleUpload(file);
    if (business?.logoUrl && !imageUrl) await handleRemoveImage();

    await updateBusinessInfo({
      ...business,
      ...values,
      businessId: business._id,
      logoUrl: imageUrl,
    });
    dispatch(getBusinessInfo());
  };

  const next = async () => {
    try {
      const values = await form.validateFields();
      if (current === 0) {
        trackWelcomeContinueClicked(analyticsContext);
        await firstStepHandler(values);
      }
      setCurrent(current + 1);
    } catch (error) {
      console.error("Validation Error:", error);
    }
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const formInitialValues = useMemo(
    () => ({
      ...business,
      venues: [
        {
          pocs: [
            {
              name: "",
              email: "",
              notifyEmail: false,
              phone: "",
            },
          ],
          venueWorkingHours,
        },
      ],
    }),
    [business],
  );

  return (
    <Flex ref={formContainerRef}>
      <Flex
        align="center"
        justify="center"
        vertical
        className={`onboarding-form ${isMobile ? "mobile" : ""}`}
      >
        <Loader visible={websiteLoading || businessLoading} />
        <Flex vertical className="header-section">
          <Typography.Title level={1} className={`main-title ${isMobile ? "mobile" : ""}`}>
            Get Started With Content Marketing
          </Typography.Title>
          <Typography.Title level={4} className="main-subtitle">
            Complete the setup to start connecting with creators
          </Typography.Title>
        </Flex>
        <div className="steps-container">
          {steps.map((item, index) => (
            <div key={item.title} className="steps-wrapper">
              {index > 0 && <div className={`steps-connector ${index === steps.length - 1 ? "last-connector" : ""}`} aria-hidden />}
              <div
                className={`steps-step ${isMobile ? "mobile" : ""} ${current === index ? "active" : current < index ? "wait" : "done"
                  }`}
              >
                <div className="steps-step-icon">{index + 1}</div>
                <span className="steps-step-title">{item.title}</span>
              </div>
            </div>
          ))}
        </div>
        <Card className={`form-card ${isMobile ? "mobile" : ""}`}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={formInitialValues}
            className="onboarding-form-container"
          >
            <div ref={formContentRef} className="form-content-wrapper">
              {steps[current].content}
            </div>

            <div
              className={`button-container ${current > 0 ? "with-previous" : ""}`}
            >
              {current > 0 && (
                <Button
                  className="previous-button"
                  onClick={prev}
                  disabled={websiteLoading}
                >
                  Previous
                </Button>
              )}
              {current < steps.length - 1 && (
                <Button
                  type="primary"
                  onClick={next}
                  disabled={websiteLoading}
                  loading={websiteLoading}
                >
                  Continue
                </Button>
              )}
              {current === steps.length - 1 && (
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={websiteLoading}
                  loading={websiteLoading}
                >
                  Continue
                </Button>
              )}
            </div>
          </Form>
        </Card>
        {isMobile && freemium && (
          <div className="upgrade-button-container">
            <Button
              type="primary"
              style={{
                transitionProperty:
                  "color, background-color, border-color, text-decoration-color, fill, stroke, -webkit-text-decoration-color",
                transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                transitionDuration: "150ms",
              }}
              className="transition-all"
              onClick={() =>
                openPaywall({ source: PaywallSource.UpgradePlan })
              }
            >
              <FontAwesomeIcon icon={faCrown} />
              Upgrade to unlock full access
            </Button>
          </div>
        )}
      </Flex>
    </Flex>
  );
};

export default OnboardingForm;
